<template>
    <div class="main-block request-management">
        <Breadcrumbs />
        <div class="div col-12 p-0 d-flex align-items-start">
            <h4 class="">{{ $t('title.ApiLogs') }}</h4>
        </div>
        <div class="scrollable-block">
            <div class="col-12 row mt-3 m-0 p-0">
                <crud-table :columns="columns" :list-url="listUrl" ref="application-table" v-on:refresh="resetFilter">
                    <template #filter-slot>
                        <b-col lg="2" class="mt-3 mt-md-0">
                            <b-form-group :label="$t('title.services')" label-for="type-select" class="mb-0">
                                <treeselect :multiple="false" @input="handleFilter" v-model="filters.service"
                                            :placeholder="$t('title.select')"
                                            class="form-input"
                                            :options="services" />
                            </b-form-group>
                        </b-col>
                        <b-col lg="2" class="mt-3 mt-md-0">
                            <b-form-group :label="$t('title.api')" label-for="type-select" class="mb-0">
                                <treeselect :multiple="false" @input="handleFilter" v-model="filters.payload"
                                            :placeholder="$t('title.select')"
                                            class="form-input"
                                            :options="payloads" />
                            </b-form-group>
                        </b-col>
                        <b-col lg="2" class="mt-3 mt-md-0">
                            <label for="limit" class="mb-1" >
                                {{ $t('title.SelectLimit') }}
                            </label>
                            <b-form-datepicker id="date_picker"  @input="handleFilter" v-model="filters.date" reset-button
                                               today-button locale="en" class="w-100">
                            </b-form-datepicker>
                        </b-col>

                    </template>

                    <template #cell(api)="{ item }">
                        {{ item?.request_payload?.board_name }}
                    </template>
                    <template #cell(actions)="{ item }">
                        <b-button variant="Primary"
                            @click="openViewModal(item.id)" v-b-tooltip.hover :title="$t('title.show')" >
                            <b-icon variant="primary" icon="eye" class="icon-style"></b-icon>
                        </b-button>
                        <div class="modal fade" id="deleteModal" tabindex="-1" aria-labelledby="deleteModalLabel"
                            aria-hidden="true">
                            <div class="modal-dialog modal-dialog-centered modal-xl">
                                <div class="modal-content">
                                    <div class="modal-header">
                                        <h5 class="modal-title" id="deleteModalLabel">{{
                                            $t('title.viewApiLog') }} - <span id="apiLogId"></span>
                                        </h5>
                                    </div>
                                    <div class="modal-body">
                                        <!-- Main Content with Scrollable Details -->
                                        <b-row v-if="isLoading" class="justify-content-center my-4 scrollable-container">
                                            <b-spinner :label="$t('title.loading')"></b-spinner>
                                        </b-row>

                                        <b-row v-else-if="errorMessage" class="justify-content-center my-4 text-danger">
                                            {{ errorMessage }}
                                        </b-row>

                                        <b-row v-else class="details-container">
                                            <b-col cols="12">
                                                <strong>{{ $t('title.url') }}:</strong> {{ logDetails.url }}
                                            </b-col>
                                            <b-col cols="12" md="6" lg="4">
                                                <strong>{{ $t('title.method') }}:</strong> {{ logDetails.method }}
                                            </b-col>
                                            <b-col cols="12" md="6" lg="4">
                                                <strong>{{ $t('title.status_code') }}:</strong> {{ logDetails.status_code }}
                                            </b-col>
                                            <b-col cols="12" md="6" lg="4">
                                                <strong>{{ $t('title.service_id') }}:</strong> {{ logDetails.service_id }}
                                            </b-col>
                                            <b-col cols="12" md="6" lg="4">
                                                <strong>{{ $t('title.user_id') }}:</strong> {{ logDetails.user_id }}
                                            </b-col>
                                            <b-col cols="12" md="6" lg="4">
                                                <strong>{{ $t('title.created_at') }}:</strong> {{ formatDate(logDetails.created_at) }}
                                            </b-col>
                                            <b-col cols="12">
                                                <strong>{{ $t('title.request_payload') }}:</strong>
                                                <pre>{{ formattedJson(logDetails.request_payload) }}</pre>
                                            </b-col>
                                            <b-col cols="12">
                                                <strong>{{ $t('title.response_payload') }}:</strong>
                                                <pre>{{ formattedJson(logDetails.response_payload) }}</pre>
                                            </b-col>
                                        </b-row>
                                    </div>
                                    <div class="modal-footer">
                                        <button type="button" class="btn btn-secondary" @click="closeViewModal"> {{
                                            $t('title.close') }}</button>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </template>
                    <!-- Display Created At Time -->
                    <template #cell(created_at)="{ item }">
                        <div>
                            <span>
                                {{ formatDate(item.created_at) }}
                            </span>
                        </div>
                    </template>
                </crud-table>
            </div>
        </div>
    </div>
</template>

<script>
import { Modal } from "bootstrap";
import axios from '@/axios';
import Breadcrumbs from "@/components/Breadcrumbs.vue";
import {request} from "@/Util/Request";

const FILTER_FORM = {
    roles: null,
    statuses: null
};

const COLUMN_STATE = (self) => [
    {
        key: 'actions',
        label: self.$t('title.actions')
    },
    {
        key: 'url',
        label: self.$t('title.url'),
        sortable: true
    },
    {
        key: 'api',
        label: self.$t('title.api')
    },
    {
        key: 'method',
        label: self.$t('title.method'),
        sortable: true
    },
    {
        key: 'status_code',
        label: self.$t('title.status_code'),
        sortable: true
    },
    {
        key: 'service_id',
        label: self.$t('title.services'),
        sortable: true
    },
    {
        key: 'user_id',
        label: self.$t('title.user'),
        sortable: true
    },
    {
        key: 'created_at',
        label: self.$t('title.created_at'),
        sortable: true
    },
];

export default {
    data() {
        return {
            operation: '',
            query: {
                page: '',
                perPage: "",
            },
            pagination: {
                total: 0,
            },
            listUrl: 'admin/Api-Logs',
            filters: { ...FILTER_FORM },
            showPopup: false,
            modalInstance: null, // Declare modalInstance here
            visibleFields: {},
            logDetails: {},
            isLoading: true,
            errorMessage: null,
            services: [],
            payloads: []
        };
    },
    mounted() {
        this.fetchServices();
    },
    components: { Breadcrumbs },
    methods: {
        async fetchLogDetails(logId) {
            this.isLoading = true;

            try {
                const response = await axios.get(`admin/Api-Logs/${logId}`);
                this.logDetails = response.data.data;
            } catch (error) {
                console.error("API call error:", error);
                this.errorMessage = error.response ? error.response.data.message : error.message;
            } finally {
                this.isLoading = false;
            }
        },
        formattedJson(jsonString) {
            try {
                return JSON.stringify(JSON.parse(jsonString), null, 2);
            } catch {
                return jsonString || "-";
            }
        },
        openViewModal(Id) {
            const modalElement = document.getElementById("deleteModal");
            const apiLogIdLabel = document.getElementById("apiLogId");
            apiLogIdLabel.innerText = Id;
            this.modalInstance = new Modal(modalElement); // Initialize modal
            this.modalInstance.show();
            this.fetchLogDetails(Id);
        },
        closeViewModal() {
            if (this.modalInstance) {
                this.modalInstance.hide();
            }
        },
        formatDate(dateString) {
            const date = new Date(dateString);
            return date.toLocaleString();
        },
        handleFilter() {
            this.$refs['application-table'].handleFilter(this.filters);
        },
        resetFilter() {
            this.filters = { ...FILTER_FORM };
        },
        async fetchServices() {
            try {

                const response = await request({
                    method: 'get',
                    url: `/admin/Api-Log/getServices`,
                });

                this.services = response.data;
                this.payloads = response.records.map(record => ({
                    id: record.board_name,
                    label: record.board_name
                }));
                // this.payloads = response.records;
            } catch (error) {
                console.log(error);
            }
        },

    },
    computed: {
        columns() {
            return COLUMN_STATE(this);
        }
    }
};
</script>

<style scoped>
.header {
    flex-shrink: 0;
    display: flex;
    justify-content: space-between;
    padding: 10px;
}

.main-block {
    height: auto;
    min-height: 100%;
}

.details-container {
    background: #f8f9fa;
    padding: 10px;
    border-radius: 10px;
    box-shadow: 0px 0px 10px rgba(0, 0, 0, 0.1);
    margin: 0;
    min-height: 400px;
    width: 100%;
}

pre {
    border-radius: 5px;
    white-space: pre-wrap;
    word-break: break-word;
    border: 1px solid #ddd;
    padding: 10px;
    overflow-x: auto;
}
</style>
